if (!customElements.get('fbt-bundle')) {
  class FbtBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.items = Array.from(this.querySelectorAll('[data-fbt-item]'));
      this.submitButton = this.querySelector('[data-fbt-submit]');
      this.label = this.querySelector('[data-fbt-label]');
      this.total = this.querySelector('[data-fbt-total]');
      this.status = this.querySelector('[data-fbt-status]');
      this.defaultLabel = this.label ? this.label.textContent : 'Add selected to cart';

      this.addEventListener('change', this.handleChange.bind(this));
      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.handleSubmit.bind(this));
      }

      this.updateSummary();
    }

    get selectedItems() {
      return this.items.filter((item) => item.checked && !item.disabled);
    }

    formatMoney(cents) {
      const currency = this.dataset.currency
        || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
        || 'USD';

      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
          style: 'currency',
          currency
        }).format(cents / 100);
      } catch (error) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    handleChange(event) {
      if (!event.target.matches('[data-fbt-item]')) return;
      this.updateSummary();
    }

    updateSummary() {
      const selected = this.selectedItems;
      const totalCents = selected.reduce(
        (sum, item) => sum + Number(item.dataset.price || 0),
        0
      );

      this.items.forEach((item) => {
        const card = item.closest('.fbt-bundle__item');
        if (card) card.classList.toggle('is-selected', item.checked && !item.disabled);
      });

      if (this.total) this.total.textContent = this.formatMoney(totalCents);
      if (this.submitButton) {
        this.submitButton.disabled = selected.length === 0;
        this.submitButton.setAttribute(
          'aria-label',
          selected.length
            ? `Add ${selected.length} selected item${selected.length === 1 ? '' : 's'} to cart`
            : 'Select at least one item'
        );
      }
      if (this.label && !this.submitButton.classList.contains('is-success')) {
        this.label.textContent = selected.length ? this.defaultLabel : 'Select items';
      }
      this.setStatus('');
    }

    setStatus(message, isError = false) {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-error', isError);
    }

    async handleSubmit() {
      const selected = this.selectedItems;
      if (!selected.length || !this.submitButton) return;
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

      if (window.theme && theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.submitButton.setAttribute('aria-disabled', 'true');
      this.submitButton.classList.add('is-loading');
      this.setStatus('');

      const cartDrawer = document.querySelector('cart-drawer');
      const sections = ['cart-icon-bubble'];
      if (cartDrawer) {
        const drawerSection = cartDrawer.closest('.shopify-section');
        if (drawerSection) {
          sections.push(drawerSection.id.replace('shopify-section-', ''));
        }
      }

      const items = selected.map((item) => ({
        id: Number(item.value),
        quantity: 1
      }));
      const cartAddUrl = (window.theme && theme.routes && theme.routes.cartAdd) || '/cart/add';
      const cartUrl = (window.theme && theme.routes && theme.routes.cart) || '/cart';

      try {
        const response = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            items,
            sections: sections.join(','),
            sections_url: window.location.pathname
          })
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || 'Unable to add the selected items.');
        }

        if (window.theme && theme.settings.afterAtc === 'page') {
          window.location.href = cartUrl;
          return;
        }

        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && data.sections) {
          const openDrawer = window.theme
            && (theme.settings.afterAtc === 'drawer'
              || (theme.settings.afterAtc === 'drawer_desktop'
                && theme.mediaMatches
                && theme.mediaMatches.md));
          cartDrawer.renderContents(data, openDrawer);
        } else if (data.sections && data.sections['cart-icon-bubble']) {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble) {
            const template = document.createElement('template');
            template.innerHTML = data.sections['cart-icon-bubble'];
            const replacement = template.content.querySelector('#cart-icon-bubble');
            if (replacement) bubble.replaceWith(replacement);
          }
        }

        let cart = null;
        try {
          const cartResponse = await fetch(`${cartUrl}.js`);
          if (cartResponse.ok) cart = await cartResponse.json();
        } catch (error) {
          cart = null;
        }

        document.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: {
            cart,
            variantIds: items.map((item) => item.id)
          }
        }));

        this.submitButton.classList.add('is-success');
        if (this.label) this.label.textContent = 'Added to cart';
        this.setStatus(`${items.length} item${items.length === 1 ? '' : 's'} added to cart.`);
      } catch (error) {
        this.setStatus(error.message || 'Unable to add the selected items.', true);
        document.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: error.message }
        }));
      } finally {
        this.submitButton.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitButton.classList.remove('is-loading', 'is-success');
          if (this.label) this.label.textContent = this.defaultLabel;
          this.updateSummary();
        }, 1400);
      }
    }
  }

  customElements.define('fbt-bundle', FbtBundle);
}
